import { invokeSupabaseFunction } from "../lib/supabaseApi";
import type { GeminiAttachedFile } from "./FileAttachmentService";
import { logger } from "../lib/logger";
import { getSupportProduct } from "./supportProduct";

/** Tâches acceptées par l'edge function support-ai (cf. support-ai/logic.ts). */
type SupportAiTask =
  | "chat_reply"
  | "chat_classify"
  | "kb_keywords"
  | "ticket_title"
  | "ticket_analysis"
  | "screenshot"
  | "agent_reply"
  | "ticket_classify"
  | "urgency"
  | "reply_templates";

interface SupportAiResponse {
  success: boolean;
  data?: string;
  error?: string;
  message?: string;
}
// Bases de connaissances injectées dans le prompt de chat support, par produit.
// Sources de vérité : Docs/SUPPORT/knowledge-base.json (date) et
// Docs/SUPPORT/knowledge-base-form.json (form). Le produit courant est déclaré
// par chaque app via setSupportProduct() (cf. supportProduct.ts).
import supportKnowledgeBase from "../../../../Docs/SUPPORT/knowledge-base.json";
import formKnowledgeBase from "../../../../Docs/SUPPORT/knowledge-base-form.json";

interface KBFeature {
  description?: string;
  procedures?: string[];
  commonIssues?: string[];
}

/**
 * Nombre d'items conservés par section quand la question ne matche rien :
 * c'est le socle « vue d'ensemble » du prompt. Historiquement 5, monté à 8
 * après mesure (cf. plus bas).
 */
const KB_BASE_ITEMS_PER_LIST = 8;

/**
 * Plafond par section une fois la pertinence prise en compte. Borne le pire
 * cas (une question qui matche tout) à un prompt qui reste petit.
 *
 * Mesure du 2026-09-21 sur Docs/SUPPORT/knowledge-base.json (169 items) :
 *   - slice(0, 5) historique : 9 814 caractères ≈ 2,8k tokens
 *   - KB entière, sans troncature : 22 087 caractères ≈ 6,3k tokens
 * Le modèle réellement appelé est `VERTEX_MODEL` (supabase/functions/hyper-task/handler-utils.ts,
 * gemini-2.5-flash) ; `gemini-constants.json` (`gemini-3.1-flash-lite`) n'est qu'un libellé côté client
 * — le nom y est indicatif, pas la source de vérité. Fenêtre de contexte 1M
 * tokens : la KB complète pèse ~0,6 % du contexte. Le commentaire d'origine
 * (« pour ne pas exploser le contexte ») datait de gemini-2.0-flash et n'est
 * plus le facteur limitant. Ce qui reste vrai : on paie ces tokens à CHAQUE
 * message du chat, et noyer la réponse dans 169 items dégrade la précision.
 * D'où une sélection par pertinence plutôt qu'un envoi intégral.
 */
const KB_MAX_ITEMS_PER_LIST = 14;

/** Mots vides FR/EN — ignorés dans le calcul de pertinence. */
const KB_STOPWORDS = new Set([
  "les",
  "des",
  "une",
  "uns",
  "est",
  "sont",
  "pour",
  "avec",
  "dans",
  "que",
  "qui",
  "quoi",
  "comment",
  "pourquoi",
  "mon",
  "mes",
  "ton",
  "tes",
  "son",
  "ses",
  "nos",
  "vos",
  "leur",
  "leurs",
  "cette",
  "cet",
  "ces",
  "the",
  "and",
  "for",
  "with",
  "how",
  "why",
  "can",
  "you",
  "pas",
  "plus",
  "mais",
  "donc",
  "sur",
  "par",
  "aux",
  "ont",
  "fait",
  "faire",
  "peux",
  "peut",
  "puis",
  "quand",
  "tout",
  "tous",
  "toute",
  "toutes",
  "etre",
  "avoir",
  "aussi",
  "bien",
  "chez",
  "elle",
  "ils",
  "nous",
  "vous",
  "voir",
  "veux",
  "veut",
  "besoin",
]);

/**
 * Normalise un texte pour la comparaison : minuscules, sans accents.
 */
function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Extrait les racines de mots significatives d'une question utilisateur.
 * Troncature à 6 caractères : racine grossière mais suffisante pour que
 * « sondages » matche « sondage », « exporter » matche « export »,
 * « invitations » matche « invitation ».
 */
export function extractQueryTerms(query: string): string[] {
  const words = normalizeForSearch(query).match(/[a-z0-9]{3,}/g) || [];
  const stems = new Set<string>();
  for (const word of words) {
    if (KB_STOPWORDS.has(word)) continue;
    stems.add(word.slice(0, 6));
  }
  return [...stems];
}

/**
 * Sélectionne les items d'une liste (procédures ou problèmes connus) à
 * injecter dans le prompt.
 *
 * Contrat — c'est ce qui corrige le biais de position :
 *  1. tout item qui matche au moins un terme de la question est candidat,
 *     QUELLE QUE SOIT sa position dans le tableau (les entrées ajoutées par
 *     le pipeline de propositions arrivent toujours en fin de tableau) ;
 *  2. les candidats sont triés par nombre de termes matchés (décroissant),
 *     à score égal l'ordre du tableau est conservé ;
 *  3. on complète avec le début du tableau jusqu'à KB_BASE_ITEMS_PER_LIST
 *     pour garder une vue d'ensemble sur les questions vagues — le
 *     comportement historique reste donc un sous-ensemble du nouveau.
 */
export function selectKBItems(items: string[], queryTerms: string[]): string[] {
  if (items.length === 0) return [];

  const scored = items.map((text, index) => {
    const haystack = normalizeForSearch(text);
    let score = 0;
    for (const term of queryTerms) {
      if (haystack.includes(term)) score++;
    }
    return { text, index, score };
  });

  const relevant = scored
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, KB_MAX_ITEMS_PER_LIST);

  const picked = new Set(relevant.map((item) => item.index));

  // Complément « vue d'ensemble » : début du tableau, sans doublon.
  for (const item of scored) {
    if (picked.size >= KB_BASE_ITEMS_PER_LIST) break;
    picked.add(item.index);
  }

  // Restitution dans l'ordre du tableau : la KB est rédigée en séquence
  // (les procédures se lisent dans l'ordre).
  return scored.filter((item) => picked.has(item.index)).map((i) => i.text);
}

/**
 * Construit un bloc textuel de la KB à injecter dans le prompt Gemini.
 *
 * @param kb    KB brute (Docs/SUPPORT/knowledge-base*.json)
 * @param query Message de l'utilisateur. Sans query, on retombe sur les
 *              KB_BASE_ITEMS_PER_LIST premiers items de chaque section.
 */
export function buildKBContext(kb: unknown, query = ""): string {
  const features = (kb as { features?: Record<string, KBFeature> }).features;
  if (!features) return "";
  const queryTerms = extractQueryTerms(query);
  const blocks: string[] = [];
  for (const [name, data] of Object.entries(features)) {
    const procs = selectKBItems(data.procedures || [], queryTerms)
      .map((p) => `  - ${p}`)
      .join("\n");
    const issues = selectKBItems(data.commonIssues || [], queryTerms)
      .map((i) => `  - ${i}`)
      .join("\n");
    blocks.push(
      `### ${name}\n${data.description || ""}\nProcédure :\n${procs}\nProblèmes connus :\n${issues}`.trim(),
    );
  }
  return blocks.join("\n\n");
}

const KB_SOURCES: Record<string, unknown> = {
  date: supportKnowledgeBase,
  form: formKnowledgeBase,
};

/** KB filtrée pour un produit et une question donnés. */
export function getKBContext(product: string, query = ""): string {
  return buildKBContext(KB_SOURCES[product] ?? KB_SOURCES.date, query);
}

/** Identité de l'assistant selon le produit (visible dans le ton des réponses). */
const CHAT_IDENTITIES: Record<string, string> = {
  date: "Tu es l'assistant de chat DooDates (sondages de dates et questionnaires).",
  form: "Tu es l'assistant de chat DooQuest (création de formulaires et sondages en ligne).",
};

interface KBArticle {
  title: string;
  content: string;
  keywords: string[];
}

// Articles KB suggérés par le widget pour DooQuest (form-polls).
// Le set date historique reste inline dans searchKBArticles.
const FORM_KB_ARTICLES: KBArticle[] = [
  {
    title: "Comment créer un formulaire",
    content: `
Pour créer un formulaire avec DooQuest :

1. **Allez sur "Créer"** (/create)
2. **Deux méthodes** :
   - **Par IA** : décrivez votre besoin en langage naturel, ex. "Crée un questionnaire de satisfaction client avec un NPS"
   - **Manuellement** : ajoutez vos questions une à une dans l'éditeur
3. **8 types de questions** : choix unique, choix multiples, texte court, texte long, matrice (échelle Likert), note, NPS, date
4. **Options avancées** : logique conditionnelle (afficher une question selon une réponse précédente), formulaire multi-étapes
5. **Publiez** pour obtenir le lien de partage

🔧 **Problèmes courants** :
- **Formulaire perdu après un refresh** : une sauvegarde automatique locale existe, retournez sur /create
- **IA indisponible** : réessayez plus tard ou créez manuellement
- **Quota IA épuisé** (5 actions à vie en gratuit) : l'édition manuelle reste illimitée, l'IA se débloque avec DooQuest Pro
    `,
    keywords: ["créer", "formulaire", "nouveau", "questions", "ia", "types"],
  },
  {
    title: "Partager un formulaire et collecter des réponses",
    content: `
Pour partager votre formulaire DooQuest :

**1. Récupérer le lien**
- Après publication, un lien unique est généré
- Copiez-le depuis le dashboard ou la page du formulaire

**2. Diffuser**
- Email, WhatsApp, réseaux sociaux, ou QR code
- Les répondants répondent **sans créer de compte**

**3. Suivre les réponses**
- Les réponses apparaissent en temps réel sur le dashboard

🔧 **Problèmes courants** :
- **Lien ne fonctionne pas** : vérifiez que le formulaire est bien publié (pas en brouillon) et que le lien est complet
- **Erreur 404** : le formulaire a peut-être été archivé ou supprimé — re-copiez le lien depuis le dashboard
- **"Trop de soumissions"** : une limite anti-abus s'applique par connexion, réessayez dans quelques minutes
    `,
    keywords: [
      "partager",
      "lien",
      "répondre",
      "inviter",
      "répondants",
      "404",
      "qr",
    ],
  },
  {
    title: "Consulter et exporter les réponses",
    content: `
Pour consulter et exporter les réponses de votre formulaire DooQuest :

**1. Page Résultats**
- Ouvrez votre formulaire depuis le dashboard → onglet "Résultats"
- Réponses en temps réel, statistiques par question
- **Analyse IA** : insights automatiques (tendances, NPS, synthèses)

**2. Exporter**
- Bouton "Exporter" sur la page Résultats
- Formats : **CSV** (Excel/Sheets), **PDF** (partage/impression), **JSON** (intégrations), **Markdown** (documentation)

🔧 **Problèmes courants** :
- **Bouton export grisé** : vous n'avez pas encore de réponse
- **CSV mal affiché sous Excel** : le fichier est en UTF-8, passez par "Importer" si besoin
- **Page Résultats vide** : rechargez la page ; si ça persiste, contactez le support
    `,
    keywords: [
      "export",
      "csv",
      "pdf",
      "résultats",
      "réponses",
      "télécharger",
      "grisé",
      "analyse",
    ],
  },
  {
    title: "Problèmes d'authentification",
    content: `
Pour résoudre les problèmes d'authentification sur DooQuest :

**1. Connexion simple**
- **Google** : connexion en 1 clic
- **Email** : connexion par email, pas de mot de passe à mémoriser

**2. Problèmes courants**

**"Erreur Google"**
→ Vérifiez que vous êtes bien connecté à Google
→ Essayez en navigation privée
→ Autorisez les cookies si nécessaire

**"Session expirée"**
→ Reconnectez-vous simplement

🔧 **Si rien ne fonctionne** :
- Videz le cache de votre navigateur
- Essayez un autre navigateur
- Contactez le support via le widget (en bas à droite)
    `,
    keywords: [
      "login",
      "connexion",
      "mot de passe",
      "google",
      "session",
      "authentification",
    ],
  },
  {
    title: "Offres et quota IA",
    content: `
Les offres DooQuest :

**Gratuit**
- Formulaires et réponses **illimités**
- 5 actions IA à vie (création/édition par IA)
- L'édition manuelle reste toujours illimitée

**DooQuest Pro — 70 €/an**
- IA illimitée (dans le cadre d'un usage raisonnable)
- Exports avancés
- Rétention illimitée des données

**S'abonner / gérer**
- Page Tarifs : /pricing
- Abonnement et factures : /account/subscription

🔧 **Problèmes courants** :
- **Quota IA atteint** : continuez en édition manuelle, ou passez à Pro
- **Abonnement non reconnu après paiement** : déconnectez-vous puis reconnectez-vous ; sinon contactez le support avec l'email de confirmation
    `,
    keywords: [
      "prix",
      "tarif",
      "gratuit",
      "pro",
      "quota",
      "crédit",
      "abonnement",
      "payant",
    ],
  },
  {
    title: "Données des répondants et RGPD",
    content: `
Gestion des données sur DooQuest :

**Votre responsabilité**
- En collectant des réponses via vos formulaires, **vous êtes responsable des données** que vous collectez auprès de vos répondants

**Supprimer des données**
- Une réponse : depuis la page Résultats du formulaire
- Un formulaire et ses réponses : depuis le dashboard
- Tout votre compte et vos données : depuis /data-control

**Sécurité**
- Échanges chiffrés (HTTPS)
- Les données ne sont jamais vendues ni partagées avec des tiers

🔧 **Cas courants** :
- **Un répondant demande la suppression de sa réponse** : supprimez-la depuis la page Résultats, ou contactez le support
    `,
    keywords: [
      "rgpd",
      "données",
      "supprimer",
      "confidentialité",
      "répondant",
      "sécurité",
    ],
  },
];

/**
 * Tout appel IA du support passe par ici, vers l'edge function `support-ai`.
 *
 * Le support a sa propre fonction, séparée de `hyper-task` (génération de
 * sondages) : il ne débite jamais les crédits IA des utilisateurs — via
 * hyper-task, une seule conversation de support épuisait les 3 crédits à vie
 * d'un compte Free (mesuré le 29/09/2026). Le coût est pour DooDates :
 * support-ai journalise chaque appel, applique un plafond par personne et un
 * plafond global par jour, et alerte les admins par e-mail
 * (migration 20260929190000_support_ai_isolation).
 *
 * Plafond atteint, IA coupée ou erreur : `success: false`, et chaque appelant
 * retombe sur son repli (le ticket reste toujours possible).
 */
async function generateSupportContent(
  task: SupportAiTask,
  userInput: string,
  prompt?: string,
  attachedFile?: GeminiAttachedFile,
): Promise<SupportAiResponse> {
  try {
    const res = await invokeSupabaseFunction<SupportAiResponse>(
      "support-ai",
      {
        task,
        userInput,
        prompt,
        ...(attachedFile
          ? {
              attachedFile: {
                mimeType: attachedFile.mimeType,
                contentBase64: attachedFile.contentBase64,
              },
            }
          : {}),
      },
      { timeout: 20000, requireAuth: false },
    );
    if (res?.success && typeof res.data === "string") {
      return { success: true, data: res.data };
    }
    return {
      success: false,
      error: res?.error ?? "API_ERROR",
      message: res?.message,
    };
  } catch (err) {
    return {
      success: false,
      error: "API_ERROR",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

export interface AIAnalysisResult {
  classification: "Bug" | "Question" | "Feature" | "Feedback";
  severity: "Low" | "Normal" | "High" | "Critical";
  sentiment: string;
  summary: string;
  diagnostic?: string;
  suggestedAction?: string;
  suggestedResponse?: string;
}

export interface AgentResponse {
  suggestedResponse: string;
  tone: "empathetic" | "professional" | "friendly" | "urgent";
  confidence: number; // 0-1
  templateUsed?: string;
  personalizationNotes?: string;
}

export interface TicketClassification {
  category: string;
  priority: "low" | "medium" | "high" | "urgent";
  estimatedResolutionTime: number; // en heures
  suggestedAgent?: string; // si assignation automatique
}

export interface UrgencyDetection {
  isUrgent: boolean;
  urgencyLevel: "low" | "medium" | "high" | "critical";
  reasons: string[];
  recommendedAction: string;
  escalationNeeded: boolean;
}

export class SupportAIService {
  private static instance: SupportAIService;

  // SLA en jours comme défini dans le plan
  private static readonly SLA_DAYS = {
    blocker: 1, // 1 jour
    critical: 2, // 2 jours
    normal: 5, // 5 jours
    low: 10, // 10 jours
  };

  public static getInstance(): SupportAIService {
    if (!SupportAIService.instance) {
      SupportAIService.instance = new SupportAIService();
    }
    return SupportAIService.instance;
  }

  /**
   * Obtenir le SLA en jours pour une sévérité donnée
   */
  public static getSLADays(severity: string): number {
    return (
      SupportAIService.SLA_DAYS[
        severity as keyof typeof SupportAIService.SLA_DAYS
      ] || 5
    );
  }

  /**
   * Obtenir le texte SLA formaté pour l'affichage
   */
  public static getSLAText(severity: string): string {
    const days = SupportAIService.getSLADays(severity);
    return days === 1 ? `${days} jour` : `${days} jours`;
  }

  /**
   * Génère un titre court et descriptif pour un ticket
   * @param userMessage Message initial de l'utilisateur
   * @param aiAnalysis Analyse IA optionnelle pour enrichir le titre
   * @returns Titre court (max 60 caractères)
   */
  async generateTicketTitle(
    userMessage: string,
    aiAnalysis?: AIAnalysisResult | null,
  ): Promise<string> {
    try {
      const prompt = `
        Tu es un expert en support technique. Génère un titre court et descriptif pour ce ticket de support.
        
        MESSAGE UTILISATEUR:
        "${userMessage}"
        
        ${aiAnalysis ? `CLASSIFICATION: ${aiAnalysis.classification}` : ""}
        ${aiAnalysis ? `RÉSUMÉ: ${aiAnalysis.summary}` : ""}
        
        CONSIGNES:
        - Maximum 60 caractères
        - Clair et descriptif
        - Commence par un verbe d'action si possible (ex: "Impossible de...", "Erreur lors de...", "Comment...")
        - Pas de préfixe [Bug], [Question], etc.
        - Exemples:
          * "Impossible d'exporter le sondage en CSV"
          * "Erreur 500 lors de la création d'un poll"
          * "Comment inviter des participants ?"
          * "Le bouton Exporter est grisé"
        
        RÉPONDS UNIQUEMENT AVEC LE TITRE, RIEN D'AUTRE.
      `;

      const response = await generateSupportContent(
        "ticket_title",
        userMessage,
        prompt,
      );

      if (!response.success || !response.data) {
        // Fallback: utiliser le début du message utilisateur
        return (
          userMessage.substring(0, 60).trim() +
          (userMessage.length > 60 ? "..." : "")
        );
      }

      const title = response.data.trim();
      return title.substring(0, 60);
    } catch (error) {
      logger.error("Error generating ticket title", "api", { error });
      // Fallback: utiliser le début du message utilisateur
      return (
        userMessage.substring(0, 60).trim() +
        (userMessage.length > 60 ? "..." : "")
      );
    }
  }

  /**
   * Analyse un screenshot avec Gemini Vision
   * @param screenshotBase64 Screenshot en base64
   * @param userMessage Message de l'utilisateur pour contexte
   * @returns Diagnostic visuel du screenshot
   */
  async analyzeScreenshot(
    screenshotBase64: string,
    userMessage: string,
  ): Promise<string | null> {
    try {
      const prompt = `
        Tu es un expert en analyse d'interface utilisateur pour DooDates (application de sondages de dates).
        Analyse ce screenshot et le message de l'utilisateur pour identifier le problème.
        
        MESSAGE UTILISATEUR:
        "${userMessage}"
        
        CONSIGNES:
        1. Décris ce que tu vois dans le screenshot (boutons, états, messages d'erreur)
        2. Identifie le problème visuel potentiel
        3. Propose un diagnostic technique précis
        4. Sois concis et factuel (max 200 caractères)
        
        Exemples de diagnostics:
        - "Le bouton 'Exporter' est grisé car aucune date n'est sélectionnée"
        - "Message d'erreur visible: 'Session expirée' - problème d'authentification"
        - "La page est blanche - erreur JavaScript probable"
        
        RÉPONDS UNIQUEMENT AVEC LE DIAGNOSTIC, RIEN D'AUTRE.
      `;

      // Extraire le base64 pur (sans le préfixe data:image/png;base64,)
      const base64Content = screenshotBase64.includes(",")
        ? screenshotBase64.split(",")[1]
        : screenshotBase64;

      // Créer l'objet GeminiAttachedFile pour Vision AI
      const imageAttachment: import("./FileAttachmentService").GeminiAttachedFile =
        {
          name: "screenshot.png",
          mimeType: "image/png",
          size: base64Content.length,
          contentBase64: base64Content,
        };

      // Appeler Gemini Vision via SecureGeminiService
      const response = await generateSupportContent(
        "screenshot",
        userMessage,
        prompt,
        imageAttachment,
      );

      if (!response.success || !response.data) {
        logger.warn("Failed to analyze screenshot with Vision AI", "api");
        return "Screenshot capturé - Analyse visuelle non disponible";
      }

      return response.data.trim().substring(0, 200);
    } catch (error) {
      logger.error("Error analyzing screenshot", "api", { error });
      return "Screenshot capturé pour analyse par l'équipe support";
    }
  }

  /**
   * Analyse un ticket via Gemini
   * @param userMessage Message initial de l'utilisateur
   * @param context Contexte capturé (logs, erreurs, url)
   */
  async analyzeTicket(
    userMessage: string,
    context: { logs: any[]; errors: any[]; url: string },
  ): Promise<AIAnalysisResult | null> {
    try {
      const logsStr = JSON.stringify(context.logs?.slice(-20) || [], null, 2);
      const errorsStr = JSON.stringify(
        context.errors?.slice(-10) || [],
        null,
        2,
      );

      const prompt = `
        Tu es un expert support technique pour l'application DooDates (sondages de dates et questionnaires).
        Analyse la demande suivante et les données techniques associées pour aider l'équipe support.

        MESSAGE UTILISATEUR:
        "${userMessage}"

        URL DE LA PAGE:
        "${context.url}"

        DERNIERS LOGS CONSOLE (JSON):
        ${logsStr}

        DERNIÈRES ERREURS CAPTURÉES (JSON):
        ${errorsStr}

        CONSIGNES:
        1. Classifie le type de ticket (Bug, Feature, Question, Feedback).
        2. Évalue la sévérité (Low, Normal, High, Critical).
        3. Détermine le sentiment utilisateur (ex: "Frustré", "Neutre", "Urgent").
        4. Fais un résumé court du problème.
        5. Analyse les logs/erreurs pour donner un diagnostic technique précis (ex: bug identifié dans tel composant).
        6. Suggère l'action suivante pour l'agent support.
        7. Propose un brouillon de réponse empathique et utile pour l'utilisateur.

        RÉPONDS EXCLUSIVEMENT AU FORMAT JSON SUIVANT:
        {
          "classification": "Bug" | "Question" | "Feature" | "Feedback",
          "severity": "Low" | "Normal" | "High" | "Critical",
          "sentiment": "string",
          "summary": "Résumé en 1 phrase",
          "diagnostic": "Analyse technique basée sur les logs",
          "suggestedAction": "Action recommandée",
          "suggestedResponse": "Texte de la réponse suggérée"
        }
      `;

      const response = await generateSupportContent(
        "ticket_analysis",
        userMessage,
        prompt,
      );

      console.log("--- SupportAIService DEBUG ---");
      console.log("Success:", response.success);
      if (!response.success || !response.data) {
        console.error("Gemini Error:", response.error, response.message);
        logger.error("Failed to analyze ticket with AI", "api", {
          error: response.error,
          message: response.message,
        });
        return null;
      }

      // Parser le JSON de la réponse Gemini
      try {
        const text = response.data;
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          console.error(
            "Impossible d'extraire le JSON de la réponse Gemini:",
            text,
          );
          return null;
        }
        return JSON.parse(jsonMatch[0]);
      } catch (parseError) {
        logger.error("Failed to parse AI response JSON", "api", {
          data: response.data,
        });
        return null;
      }
    } catch (error) {
      logger.error("Error in SupportAIService.analyzeTicket", "api", { error });
      return null;
    }
  }

  /**
   * Suggère des solutions KB avant la création d'un ticket
   * @param userMessage Message de l'utilisateur
   * @returns Liste de solutions KB pertinentes
   */
  async suggestKBSolutions(userMessage: string): Promise<Array<{
    title: string;
    content: string;
    relevance: number;
  }> | null> {
    try {
      console.log("🔍 Recherche KB pour:", userMessage);

      // Utiliser l'IA pour extraire les mots-clés et le contexte
      const prompt = `
        Analyse ce message utilisateur et extrais les mots-clés principaux pour chercher dans une base de connaissances.
        
        MESSAGE: "${userMessage}"
        
        CATÉGORIES DISPONIBLES:
        - Création de sondage
        - Invitation de participants
        - Export de résultats
        - Authentification
        - Export calendrier
        
        RÉPONDS AU FORMAT JSON:
        {
          "keywords": ["mot1", "mot2", "mot3"],
          "category": "catégorie la plus pertinente",
          "problem_type": "description courte du problème"
        }
      `;

      const response = await generateSupportContent(
        "kb_keywords",
        userMessage,
        prompt,
      );

      if (!response.success || !response.data) {
        logger.warn("Failed to extract KB keywords", "api");
        return null;
      }

      // Parser la réponse JSON
      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        return null;
      }

      const analysis = JSON.parse(jsonMatch[0]);
      console.log("📝 KB Keywords:", analysis);

      // Rechercher dans la KB (simulé pour l'instant - à implémenter avec vraie recherche)
      const kbSuggestions = this.searchKBArticles(
        analysis.keywords,
        userMessage,
      );

      return kbSuggestions;
    } catch (error) {
      logger.error("Error suggesting KB solutions", "api", { error });
      return null;
    }
  }

  /**
   * Recherche dans les articles KB (version simplifiée)
   * TODO: Implémenter avec vraie recherche sémantique via Supabase
   */
  private searchKBArticles(
    keywords: string[],
    userMessage: string,
  ): Array<{
    title: string;
    content: string;
    relevance: number;
  }> {
    const message = userMessage.toLowerCase();
    const results: Array<{
      title: string;
      content: string;
      relevance: number;
    }> = [];

    // Articles KB date historiques (le set form est défini au niveau
    // module : FORM_KB_ARTICLES). TODO commun : vraie recherche Supabase.
    const dateKbArticles: KBArticle[] = [
      {
        title: "Comment créer un sondage de dates",
        content: `
Pour créer un sondage de dates avec DooDates :

1. **Cliquez sur "Créer un sondage"** sur la page d'accueil
2. **Choisissez "Sondage de dates"** dans les options
3. **Ajoutez vos propositions de dates** :
   - Cliquez sur les dates dans le calendrier
   - Ou utilisez l'IA en disant : "Crée-moi un sondage pour le week-end prochain"
4. **Personnalisez les options** :
   - Ajoutez une description
   - Choisissez les limites de temps
   - Définissez si les participants peuvent voir les votes des autres
5. **Partagez votre sondage** :
   - Copiez le lien généré
   - Envoyez-le par email, WhatsApp, ou autre
   - Les participants votent en un clic

💡 **Conseil pro** : Vous pouvez aussi utiliser l'IA directement en disant 
"Organise une réunion mardi prochain avec l'équipe marketing" !

🔧 **Problèmes courants** :
- **Export grisé** : Attendez d'avoir au moins un vote
- **Erreur 500** : Rechargez la page et réessayez
- **Dates incorrectes** : Vérifiez votre fuseau horaire en haut à droite
        `,
        keywords: ["créer", "sondage", "dates", "nouveau"],
      },
      {
        title: "Comment inviter des participants",
        content: `
Pour inviter des participants à votre sondage DooDates :

**1. Partage simple et rapide**
- Après création, un lien unique est généré automatiquement
- Cliquez sur "Copier le lien" pour le partager
- Fonctionne sur WhatsApp, email, SMS, etc.

**2. Méthodes d'invitation**
- **Email** : Envoyez le lien avec un message personnalisé
- **WhatsApp** : Partagez directement dans vos groupes
- **Réseaux sociaux** : Postez le lien sur LinkedIn, Facebook, etc.
- **QR Code** : Générez un QR code pour invitations en personne

**3. Personnalisation du message**
- Ajoutez le contexte : "Sondage pour réunion d'équipe"
- Précisez les instructions : "Votez avant vendredi 18h"
- Mentionnez les options : "Plusieurs dates possibles"

**4. Suivi des invitations**
- **Tableau de bord** : Voyez qui a voté en temps réel
- **Relance automatique** : Envoyez un rappel aux non-votants
- **Export participants** : Téléchargez la liste des emails

💡 **Conseil pro** : Pour les réunions professionnelles, 
ajoutez l'ordre du jour dans le message d'invitation !

🔧 **Problèmes courants** :
- **Lien ne fonctionne pas** : Vérifiez que le lien est complet
- **404 erreur** : Le sondage est peut-être supprimé ou privé
- **Participants ne voient pas** : Vérifiez que le sondage est public
        `,
        keywords: [
          "inviter",
          "partager",
          "lien",
          "email",
          "participants",
          "404",
        ],
      },
      {
        title: "Comment exporter les résultats",
        content: `
Pour exporter les résultats de votre sondage DooDates :

**1. Accéder à l'export**
- Allez sur la page de votre sondage
- Cliquez sur l'onglet "Résultats"
- Le bouton "Exporter" apparaît dès que vous avez des votes

**2. Formats disponibles**
- **CSV** : Idéal pour Excel/Google Sheets
  - Colonnes : Participant, Date, Vote, Timestamp
  - Parfait pour analyse personnalisée

- **PDF** : Pour partage et impression
  - Graphiques et statistiques inclus
  - Format professionnel avec en-tête

- **JSON** : Pour développeurs/intégrations
  - Structure de données complète
  - Compatible avec tous les langages

- **Markdown** : Pour documentation
  - Format lisible et versionnable
  - Idéal pour GitHub/GitLab

**3. Options d'export**
- **Tous les votes** : Export complet avec détails
- **Synthèse** : Résultats aggrégés seulement
- **Par date** : Filtre par période spécifique

**4. Automatisation**
- **Export programmé** : Tous les jours à heure fixe
- **API** : Export automatique via webhook
- **Intégrations** : Google Drive, Dropbox, etc.

💡 **Conseil pro** : Pour les rapports mensuels, 
utilisez le format CSV avec les colonnes personnalisées !

🔧 **Problèmes courants** :
- **Bouton export grisé** : Vous n'avez pas encore de votes
- **Erreur 500** : Rechargez la page et réessayez
- **Fichier vide** : Vérifiez la période d'export sélectionnée
        `,
        keywords: [
          "export",
          "csv",
          "pdf",
          "résultats",
          "télécharger",
          "grisé",
          "500",
        ],
      },
      {
        title: "Problèmes d'authentification",
        content: `
Pour résoudre les problèmes d'authentification sur DooDates :

**1. Connexion simple**
- **Google** : Connexion en 1 clic avec votre compte Google
- **Email** : Utilisez votre email professionnel ou personnel
- **Aucun mot de passe** : Pas besoin de mémoriser de nouveau mot de passe

**2. Problèmes courants et solutions**

**"Mot de passe oublié"**
→ Avec DooDates, pas de mot de passe !
→ Utilisez la connexion Google ou email magique

**"Session expirée"**
→ Reconnectez-vous simplement
→ Votre session dure 7 jours par défaut

**"Compte bloqué"**
→ Contactez le support via le widget
→ Vérifiez votre boîte de réception

**"Erreur Google"**
→ Vérifiez que vous êtes bien connecté à Google
-> Essayez en navigation privée
→ Autorisez les cookies si nécessaire

**3. Sécurité**
- **2FA optionnelle** : Double authentification disponible
- **Sessions multiples** : Connectez-vous sur plusieurs appareils
- **Historique** : Consultez vos connexions récentes

**4. Entreprises**
- **SSO** : Connexion via votre entreprise (Google Workspace, etc.)
- **Équipes** : Invitez vos collègues facilement
- **Admin** : Gérez les accès de votre équipe

💡 **Conseil pro** : Pour une utilisation professionnelle,
utilisez votre email professionnel avec la connexion Google !

🔧 **Si rien ne fonctionne** :
- Videz le cache de votre navigateur
- Essayez un autre navigateur
- Contactez le support via le widget (en bas à droite)
        `,
        keywords: [
          "login",
          "connexion",
          "mot de passe",
          "google",
          "session",
          "authentification",
        ],
      },
      {
        title: "Problèmes d'export calendrier",
        content: `
Pour résoudre les problèmes d'export calendrier avec DooDates :

**1. Export Google Calendar**
- **Prérequis** : Connectez-vous à votre compte Google
- **Méthode** :
  1. Allez sur les résultats du sondage
  2. Cliquez "Exporter vers Google Calendar"
  3. Autorisez DooDates à accéder à votre calendrier
  4. Choisissez le calendrier de destination
  5. Les dates validées sont ajoutées automatiquement

**2. Export fichier ICS**
- **Compatibilité** : Fonctionne avec Outlook, Apple Calendar, etc.
- **Méthode** :
  1. Cliquez "Exporter en ICS"
  2. Téléchargez le fichier .ics
  3. Importez-le dans votre calendrier habituel

**3. Problèmes courants**

**"Google Calendar ne se connecte pas"**
→ Vérifiez que vous êtes bien connecté à Google
→ Autorisez l'accès au calendrier dans les permissions
→ Essayez avec une autre fenêtre de navigation

**"Fuseau horaire incorrect"**
→ Vérifiez votre fuseau horaire en haut à droite
→ Assurez-vous que les dates sont correctes dans votre fuseau
→ Les participants peuvent avoir des fuseaux différents

**"Événements dupliqués"**
→ Supprimez l'export précédent avant de ré-exporter
→ Vérifiez que vous n'avez pas déjà ces dates dans votre calendrier
→ Utilisez "Supprimer les doublons" dans Google Calendar

**"Export grisé"**
→ Vous devez avoir des dates validées (choisies)
→ Le bouton n'apparaît qu'après la fin du sondage
→ Vérifiez que le sondage est bien terminé

**4. Synchronisation automatique**
- **Mise à jour** : Les changements sont synchronisés en temps réel
- **Notifications** : Recevez des alertes avant les événements
- **Partage** : Partagez le calendrier avec d'autres personnes

**5. Alternatives**
- **Copier-coller** : Copiez manuellement les dates validées
- **Export CSV** : Utilisez le CSV pour import personnalisé
- **API** : Développeurs peuvent utiliser notre API d'export

💡 **Conseil pro** : Pour les réunions récurrentes,
exportez une fois puis copiez l'événement dans Google Calendar !

🔧 **Si rien ne fonctionne** :
- Rechargez la page et réessayez
- Vérifiez votre connexion internet
- Contactez le support via le widget avec une capture d'écran
        `,
        keywords: [
          "calendrier",
          "ics",
          "google calendar",
          "outlook",
          "synchronisation",
        ],
      },
    ];

    const kbArticles =
      getSupportProduct() === "form" ? FORM_KB_ARTICLES : dateKbArticles;

    // Calculer la pertinence pour chaque article
    for (const article of kbArticles) {
      let relevance = 0;

      // Vérifier si les mots-clés de l'article matchent le message
      for (const keyword of article.keywords) {
        if (message.includes(keyword.toLowerCase())) {
          relevance += 1;
        }
      }

      // Vérifier si les keywords extraits par l'IA matchent
      for (const keyword of keywords) {
        if (
          article.keywords.some((k) =>
            k.toLowerCase().includes(keyword.toLowerCase()),
          )
        ) {
          relevance += 0.5;
        }
      }

      if (relevance > 0) {
        results.push({
          title: article.title,
          content: article.content,
          relevance: relevance / article.keywords.length, // Normaliser
        });
      }
    }

    // Trier par pertinence et retourner top 3
    return results.sort((a, b) => b.relevance - a.relevance).slice(0, 3);
  }

  /**
   * Vérifie si un ticket devrait être auto-résolu
   * @param ticket Données du ticket
   * @param aiAnalysis Analyse IA du ticket
   * @returns true si le ticket peut être auto-résolu
   */
  async shouldAutoResolve(
    ticket: { subject: string; type: string; severity: string },
    aiAnalysis: AIAnalysisResult | null,
  ): Promise<boolean> {
    // Auto-résolution uniquement pour:
    // - Questions simples (type = question)
    // - Sévérité basse ou normale
    // - Confidence IA élevée (présence de suggestedResponse)

    if (!aiAnalysis || !aiAnalysis.suggestedResponse) {
      return false;
    }

    if (ticket.type !== "question" && ticket.type !== "feedback") {
      return false;
    }

    if (ticket.severity === "critical" || ticket.severity === "blocker") {
      return false;
    }

    // Vérifier que la réponse suggérée est substantielle (>50 caractères)
    if (aiAnalysis.suggestedResponse.length < 50) {
      return false;
    }

    console.log("✅ Ticket eligible for auto-resolution");
    return true;
  }

  /**
   * Suggère une réponse pour un agent
   * @param ticket Données du ticket
   * @param context Contexte additionnel (messages précédents, profil utilisateur)
   * @returns Réponse suggérée avec métadonnées
   */
  async suggestAgentResponse(
    ticket: {
      subject: string;
      message: string;
      severity: string;
      classification?: string;
      sentiment?: string;
    },
    context?: {
      previousMessages?: Array<{
        content: string;
        sender: string;
        timestamp: string;
      }>;
      userProfile?: {
        name: string;
        email: string;
        history?: Array<{ subject: string; resolvedAt: string }>;
      };
      agentPreferences?: {
        tone: "empathetic" | "professional" | "friendly" | "urgent";
        language: "fr" | "en";
      };
    },
  ): Promise<AgentResponse | null> {
    try {
      const prompt = `
        Tu es un expert du support client pour DooDates (application de sondages de dates et questionnaires).
        Génère une réponse professionnelle et empathique pour ce ticket de support.

        INFORMATIONS TICKET:
        - Sujet: "${ticket.subject}"
        - Message: "${ticket.message}"
        - Sévérité: ${ticket.severity}
        ${ticket.classification ? `- Classification: ${ticket.classification}` : ""}
        ${ticket.sentiment ? `- Sentiment: ${ticket.sentiment}` : ""}

        ${
          context?.previousMessages
            ? `
        CONVERSATION PRÉCÉDENTE:
        ${context.previousMessages.map((msg) => `- ${msg.sender}: ${msg.content}`).join("\n")}
        `
            : ""
        }

        ${
          context?.userProfile
            ? `
        PROFIL UTILISATEUR:
        - Nom: ${context.userProfile.name}
        - Email: ${context.userProfile.email}
        ${context.userProfile.history ? `- Historique: ${context.userProfile.history.length} tickets résolus` : ""}
        `
            : ""
        }

        PRÉFÉRENCES AGENT:
        - Ton: ${context?.agentPreferences?.tone || "professional"}
        - Langue: ${context?.agentPreferences?.language || "fr"}

        CONSIGNES:
        1. Sois empathique et professionnel
        2. Adapte le ton selon les préférences de l'agent
        3. Personnalise avec le nom de l'utilisateur si disponible
        4. Sois concis mais complet (150-300 mots)
        5. Inclut des étapes claires si solution proposée
        6. Termine avec une invitation à la réponse

        RÉPONDS AU FORMAT JSON:
        {
          "suggestedResponse": "Réponse complète et personnalisée",
          "tone": "empathetic|professional|friendly|urgent",
          "confidence": 0.85,
          "templateUsed": "nom_du_template_si_utilisé",
          "personalizationNotes": "Notes sur la personnalisation appliquée"
        }
      `;

      const response = await generateSupportContent(
        "agent_reply",
        ticket.message,
        prompt,
      );

      if (!response.success || !response.data) {
        logger.warn("Failed to generate agent response suggestion", "api");
        return null;
      }

      // Parser la réponse JSON
      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        logger.error("Failed to parse agent response JSON", "api");
        return null;
      }

      const agentResponse = JSON.parse(jsonMatch[0]);

      // Validation et valeurs par défaut
      return {
        suggestedResponse:
          agentResponse.suggestedResponse || "Réponse non disponible",
        tone: agentResponse.tone || "professional",
        confidence: agentResponse.confidence || 0.5,
        templateUsed: agentResponse.templateUsed,
        personalizationNotes: agentResponse.personalizationNotes,
      };
    } catch (error) {
      logger.error("Error suggesting agent response", "api", { error });
      return null;
    }
  }

  /**
   * Génère une réponse de chat courte et conversationnelle.
   * À utiliser dans le widget pour des échanges fluides (1-2 phrases, ≤ 50 mots),
   * par opposition à suggestAgentResponse qui produit un mail de support formel.
   */
  async generateChatResponse(
    message: string,
    history?: Array<{ role: "user" | "assistant"; content: string }>,
  ): Promise<AgentResponse | null> {
    try {
      const historyBlock =
        history && history.length > 0
          ? `\nCONVERSATION PRÉCÉDENTE:\n${history
              .slice(-6)
              .map(
                (m) =>
                  `- ${m.role === "user" ? "Utilisateur" : "Assistant"}: ${m.content}`,
              )
              .join("\n")}\n`
          : "";

      // Les consignes sont envoyées via `systemInstruction` côté Edge,
      // le message utilisateur est passé séparément en user content.
      // → on ne duplique plus `message` dans le prompt.
      const product = getSupportProduct();

      // La KB est filtrée par pertinence sur la question courante (+ le
      // dernier message utilisateur, pour les relances du type « et sur
      // mobile ? »). Sans ce filtrage, seuls les premiers items de chaque
      // section arrivaient au modèle et les entrées récentes — toujours
      // ajoutées en fin de tableau par le pipeline de propositions —
      // restaient invisibles.
      const lastUserMessage =
        history
          ?.filter((m) => m.role === "user")
          .slice(-1)
          .map((m) => m.content)
          .join(" ") ?? "";
      const kbContext = getKBContext(product, `${message} ${lastUserMessage}`);

      const prompt = `
        ${CHAT_IDENTITIES[product] ?? CHAT_IDENTITIES.date}
        Réponds de façon NATURELLE et CONVERSATIONNELLE au dernier message utilisateur.

        CONNAISSANCES PRODUIT (source de vérité — utilise ce contenu pour répondre ;
        n'invente AUCUNE fonctionnalité qui n'est pas mentionnée ici) :
        ${kbContext}

        ${historyBlock}
        CONSIGNES STRICTES:
        - 1 à 2 phrases maximum, 50 mots maximum au total
        - Ton amical, direct, tutoie ou vouvoie selon l'utilisateur
        - Aucune formule de politesse ("Bonjour", "Cordialement", signature, etc.)
        - Pas de listes à puces, pas de sections, pas de markdown
        - Pas d'emojis
        - NE JAMAIS promettre qu'une fonctionnalité sera ajoutée, ni qu'elle est "en cours", ni "on va le faire", ni "je transmets à l'équipe". À la place, dis simplement que la suggestion est notée.
        - Si l'utilisateur dit au revoir / merci / bonne journée : réponds en 1 phrase courte, chaleureuse, sans relancer la discussion.
        - Si tu ne sais pas, dis-le simplement et propose de créer un ticket
        - Demandes que tu ne peux PAS traiter toi-même (parler à un humain ou à l'équipe, facturation, facture, TVA, paiement, remboursement, action sur le compte de l'utilisateur, ses données personnelles, un cas qui demande de consulter ses informations) : ne tente pas de les résoudre, dis que l'équipe peut s'en charger et que l'utilisateur peut créer un ticket avec le bouton affiché juste sous ta réponse
        - Ne dis JAMAIS « via le widget de support », « en bas à droite » ni « contactez le support » : l'utilisateur est DÉJÀ dans le widget de support, c'est lui qui affiche le bouton de ticket

        RÉPONDS AU FORMAT JSON STRICT:
        {
          "suggestedResponse": "ta réponse courte",
          "tone": "friendly",
          "confidence": 0.8
        }
      `;

      const response = await generateSupportContent(
        "chat_reply",
        message,
        prompt,
      );

      if (!response.success || !response.data) {
        logger.warn("Failed to generate chat response", "api");
        return null;
      }

      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        logger.error("Failed to parse chat response JSON", "api");
        return null;
      }

      const parsed = JSON.parse(jsonMatch[0]);
      return {
        suggestedResponse: parsed.suggestedResponse || "Réponse non disponible",
        tone: parsed.tone || "friendly",
        confidence: parsed.confidence || 0.5,
      };
    } catch (error) {
      logger.error("Error generating chat response", "api", { error });
      return null;
    }
  }

  /**
   * Classifie automatiquement un ticket entrant
   * @param message Message de l'utilisateur
   * @param subject Sujet du ticket (optionnel)
   * @returns Classification du ticket
   */
  async classifyTicket(
    message: string,
    subject?: string,
  ): Promise<TicketClassification | null> {
    try {
      const prompt = `
        Tu es un expert en classification de tickets de support pour DooDates.
        Analyse ce message et classifie-le automatiquement.

        MESSAGE UTILISATEUR:
        "${message}"
        ${subject ? `SUJET: "${subject}"` : ""}

        CATÉGORIES DISPONIBLES:
        - Création de sondage (problèmes création, configuration, options)
        - Invitation participants (partage, accès, permissions)
        - Export résultats (CSV, PDF, calendrier, formatage)
        - Authentification (connexion, compte, mot de passe)
        - Fonctionnalités (votes, dates, options avancées)
        - Facturation (abonnement, paiement, factures)
        - Autre (demandes générales, feedback)

        PRIORITÉS:
        - urgent: Problème bloquant, perte de données, sécurité
        - high: Impact fonctionnel majeur, plusieurs utilisateurs affectés
        - medium: Fonctionnalité importante, contournement possible
        - low: Amélioration, question, feedback

        TEMPS DE RÉSOLUTION ESTIMÉ (heures):
        - urgent: 1-2h
        - high: 4-8h
        - medium: 24-48h
        - low: 72-120h

        RÉPONDS AU FORMAT JSON:
        {
          "category": "catégorie_précise",
          "priority": "low|medium|high|urgent",
          "estimatedResolutionTime": 24,
          "suggestedAgent": "nom_agent_si_spécialiste_requis"
        }
      `;

      const response = await generateSupportContent(
        "ticket_classify",
        message,
        prompt,
      );

      if (!response.success || !response.data) {
        logger.warn("Failed to classify ticket", "api");
        return null;
      }

      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        logger.error("Failed to parse ticket classification JSON", "api");
        return null;
      }

      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      logger.error("Error classifying ticket", "api", { error });
      return null;
    }
  }

  /**
   * Détecte si un ticket est urgent et nécessite une action immédiate
   * @param ticket Données du ticket
   * @param context Contexte additionnel
   * @returns Détection d'urgence avec recommandations
   */
  async detectUrgency(
    ticket: {
      subject: string;
      message: string;
      severity: string;
      createdAt: string;
    },
    context?: {
      userHistory?: Array<{ ticketsCount: number; lastTicketDate: string }>;
      systemErrors?: Array<{ type: string; count: number; timestamp: string }>;
      businessImpact?: {
        affectedUsers: number;
        revenueImpact: number;
        serviceDisruption: boolean;
      };
    },
  ): Promise<UrgencyDetection | null> {
    try {
      const now = new Date();
      const ticketAge = Math.floor(
        (now.getTime() - new Date(ticket.createdAt).getTime()) /
          (1000 * 60 * 60),
      ); // en heures

      const prompt = `
        Tu es un expert en priorisation des tickets de support.
        Analyse ce ticket et détermine son niveau d'urgence.

        INFORMATIONS TICKET:
        - Sujet: "${ticket.subject}"
        - Message: "${ticket.message}"
        - Sévérité: ${ticket.severity}
        - Âge du ticket: ${ticketAge} heures

        ${
          context?.userHistory
            ? `
        HISTORIQUE UTILISATEUR:
        - Nombre de tickets: ${context.userHistory[0]?.ticketsCount || 0}
        - Dernier ticket: ${context.userHistory[0]?.lastTicketDate || "N/A"}
        `
            : ""
        }

        ${
          context?.systemErrors
            ? `
        ERREURS SYSTÈME RÉCENTES:
        ${context.systemErrors.map((err) => `- ${err.type}: ${err.count} occurrences`).join("\n")}
        `
            : ""
        }

        ${
          context?.businessImpact
            ? `
        IMPACT BUSINESS:
        - Utilisateurs affectés: ${context.businessImpact.affectedUsers}
        - Impact revenu: ${context.businessImpact.revenueImpact}€
        - Service interrompu: ${context.businessImpact.serviceDisruption ? "Oui" : "Non"}
        `
            : ""
        }

        CRITÈRES D'URGENCE:
        - Sécurité, perte de données, service interrompu = CRITICAL
        - Plusieurs utilisateurs affectés, impact majeur = HIGH
        - Fonctionnalité importante, contournement = MEDIUM
        - Question, amélioration, faible impact = LOW

        ACTIONS RECOMMANDÉES:
        - critical: Escalade immédiatement au manager
        - high: Priorité haute, assignation spécialiste
        - medium: Traitement normal dans SLA
        - low: File d'attente standard

        RÉPONDS AU FORMAT JSON:
        {
          "isUrgent": true,
          "urgencyLevel": "low|medium|high|critical",
          "reasons": ["raison1", "raison2"],
          "recommendedAction": "action_spécifique_recommandée",
          "escalationNeeded": true
        }
      `;

      const response = await generateSupportContent(
        "urgency",
        ticket.message,
        prompt,
      );

      if (!response.success || !response.data) {
        logger.warn("Failed to detect urgency", "api");
        return null;
      }

      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        logger.error("Failed to parse urgency detection JSON", "api");
        return null;
      }

      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      logger.error("Error detecting urgency", "api", { error });
      return null;
    }
  }

  /**
   * Classifie un message utilisateur et suggère l'action appropriée pour SupportWidget
   * @param userMessage Message de l'utilisateur
   * @returns Classification et action structurées
   */
  async classifyAndSuggestAction(userMessage: string): Promise<{
    classification: "bug" | "feature" | "question" | "feedback" | "complex";
    response: string;
    action: "ticket" | "roadmap" | "conversation";
    confidence: number;
  } | null> {
    try {
      // NOUVEAU PROMPT - Demande classification et réponse structurée
      const enhancedPrompt = `Tu es un expert support technique pour l'application DooDates. 

Analyse ce message utilisateur et réponds EXACTEMENT en format JSON :
{
  "classification": "bug|feature|question|feedback|complex",
  "response": "Réponse spécifique et utile pour l'utilisateur",
  "action": "ticket|roadmap|conversation",
  "confidence": 0.95
}

Règles :
- "bug" : Problème technique (erreur, ça ne marche pas, ça plante, ça bug), propose "ticket"
- "feature" : Demande ou suggestion d'ajout/amélioration de fonctionnalité. Déclencheurs : "j'aimerais", "pourriez-vous ajouter", "ce serait bien si", "serait-il possible d'avoir", "il manque", "suggestion", "idée", "ajouter", "intégration", "nouvelle fonctionnalité", "feature request". propose TOUJOURS "roadmap"
- "question" : Question d'utilisation sur une fonctionnalité EXISTANTE ("comment faire X ?"), propose "conversation"
- "feedback" : Avis général sur l'application sans demande précise, propose "conversation"
- "complex" : Cas d'usage complexe qui mélange plusieurs choses. Si l'utilisateur demande un ajout, propose "roadmap", sinon "conversation"

IMPORTANT : Si l'utilisateur exprime clairement une envie d'ajout ou d'évolution de l'app (verbe au conditionnel "j'aimerais / ce serait", ou verbe d'ajout "ajouter, intégrer, avoir"), c'est une "feature" → action "roadmap", JAMAIS "conversation".

IMPORTANT : action "ticket" (JAMAIS "conversation" ni "roadmap") dès que la demande exige une intervention humaine que l'assistant ne peut pas réaliser, même si elle est formulée poliment ou au conditionnel :
- demande explicite de parler à quelqu'un, à un humain, à l'équipe, au support ;
- facturation : facture (au nom d'une structure, avec numéro de TVA…), paiement, abonnement, remboursement ;
- action sur le compte de l'utilisateur ou ses données (suppression, changement d'e-mail, export ou effacement de données, RGPD) ;
- tout cas propre à cet utilisateur que l'assistant ne peut pas vérifier ni résoudre.
Dans ces cas, classification "complex" (ou "bug" si c'est un problème technique) et action "ticket".
Exemple : "Je voudrais parler à quelqu'un de l'équipe : notre association a besoin d'une facture à son nom avec numéro de TVA intracommunautaire." → {"classification": "complex", "action": "ticket", "confidence": 0.95, ...}

Analyse le message utilisateur fourni séparément et renvoie uniquement le JSON demandé.`;

      const response = await generateSupportContent(
        "chat_classify",
        userMessage,
        enhancedPrompt,
      );

      if (!response.success || !response.data) {
        logger.error("Failed to classify and suggest action", "api", {
          error: response.error,
          message: response.message,
        });
        return null;
      }

      // Parser la réponse JSON
      try {
        const text = response.data;
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          console.error("Impossible d'extraire le JSON de la réponse:", text);
          return null;
        }

        const parsed = JSON.parse(jsonMatch[0]);

        // Validation et normalisation
        return {
          classification: parsed.classification?.toLowerCase() || "question",
          response: parsed.response || "Réponse non disponible",
          action: parsed.action?.toLowerCase() || "conversation",
          confidence: parsed.confidence || 0.5,
        };
      } catch (parseError) {
        logger.error("Failed to parse classification response JSON", "api", {
          data: response.data,
          error: parseError,
        });
        return null;
      }
    } catch (error) {
      logger.error(
        "Error in SupportAIService.classifyAndSuggestAction",
        "api",
        { error },
      );
      return null;
    }
  }

  /**
   * Génère des templates de réponses prédéfinis
   * @param category Catégorie du ticket
   * @param tone Ton souhaité
   * @returns Templates de réponses
   */
  async generateResponseTemplates(
    category: string,
    tone:
      | "empathetic"
      | "professional"
      | "friendly"
      | "urgent" = "professional",
  ): Promise<
    Array<{
      id: string;
      title: string;
      content: string;
      variables: string[];
      usage: string;
    }>
  > {
    try {
      const prompt = `
        Génère des templates de réponses pour le support DooDates.

        CATÉGORIE: ${category}
        TON: ${tone}

        CONSIGNES:
        1. Crée 5 templates différents
        2. Utilise des variables comme {nom_utilisateur}, {nom_sondage}, {date}
        3. Adapte le ton selon la demande
        4. Inclus des cas d'usage spécifiques
        5. Sois concis et professionnel

        RÉPONDS AU FORMAT JSON:
        [
          {
            "id": "template_1",
            "title": "Titre du template",
            "content": "Contenu avec {variables}",
            "variables": ["variable1", "variable2"],
            "usage": "Quand utiliser ce template"
          }
        ]
      `;

      const response = await generateSupportContent(
        "reply_templates",
        category,
        prompt,
      );

      if (!response.success || !response.data) {
        logger.warn("Failed to generate response templates", "api");
        return [];
      }

      const jsonMatch = response.data.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        logger.error("Failed to parse response templates JSON", "api");
        return [];
      }

      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      logger.error("Error generating response templates", "api", { error });
      return [];
    }
  }
}

export const supportAIService = SupportAIService.getInstance();

// Alias utilisé par les tests unitaires (SupportAIService.debug.test.ts)
export { SupportAIService as SupportAIServiceClass };
